import React, { createContext, useContext, useState, useEffect, useRef, useMemo, useCallback } from 'react';
import useFetchSuggestedArticle from 'app/hooks/fetch/useFetchSuggestedArticle';
import { SuggestedEntry } from 'app/copied/models/ChatModules';
import useNamespacedStorage from 'app/hooks/storage/useNamespacedStorage';
import useFetchHelpCenter from 'app/hooks/fetch/useFetchHelpCenter';
import usePostVisitedArticle from 'app/hooks/mutate/usePostVisitedArticle';
import useFeatureFlags from 'app/hooks/useFeatureFlags';
import urlChangeDetector from 'app/utils/URLChangeDetector';

const SuggestedArticleContext = createContext<{
  suggestedArticle: SuggestedEntry | null;
  suggestedArticleForURL: SuggestedEntry | null;
  articlesReady: boolean;
  isLoading: boolean;
  lastViewedArticle: SuggestedEntry | null;
  setLastViewedArticle: (article: SuggestedEntry | null) => void;
  suggestedArticleEnabled: boolean;
}>({
  suggestedArticle: null,
  suggestedArticleForURL: null,
  articlesReady: false,
  isLoading: false,
  lastViewedArticle: null,
  setLastViewedArticle: () => {},
  suggestedArticleEnabled: false,
});

export const SuggestedArticleProvider = ({ children }: { children: React.ReactNode }) => {
  const { data: helpCenter } = useFetchHelpCenter();
  const [suggestedArticleEnabled, setSuggestedArticleEnabled] = useState(false);
  const flags = useFeatureFlags();

  useEffect(() => {
    if (helpCenter && flags.uiShowSuggestedArticle) {
      setSuggestedArticleEnabled(
        !!(helpCenter.next_best_action.enable_next_best_resource && flags.uiShowSuggestedArticle)
      );
    }
  }, [helpCenter, flags.uiShowSuggestedArticle]);

  const { mutate: postVisitedArticle } = usePostVisitedArticle();

  const [lastViewedArticle, setLastViewedArticle] = useNamespacedStorage<SuggestedEntry>('ostro-last-viewed-article');
  const [suggestedArticle, setSuggestedArticle] = useState<SuggestedEntry | null>(null);
  const [suggestedArticleForURL, setSuggestedArticleForURL] = useState<SuggestedEntry | null>(null);

  const { data, isLoading, isError, isFetched } = useFetchSuggestedArticle({
    enableSuggestedArticle: suggestedArticleEnabled,
  });

  const trackedArticles = useRef(new Set());

  useEffect(() => {
    if (!suggestedArticleEnabled) return;

    if (lastViewedArticle?.id && !trackedArticles.current.has(lastViewedArticle.id)) {
      postVisitedArticle({
        articleId: lastViewedArticle.id,
        withSuggestedArticle: false,
      });
      trackedArticles.current.add(lastViewedArticle.id);
    }
  }, [lastViewedArticle, postVisitedArticle, suggestedArticleEnabled]);

  useEffect(() => {
    if (data) {
      setSuggestedArticle(data);
    }
  }, [data]);

  useEffect(() => {
    if (isError) {
      setSuggestedArticle(null);
    }
  }, [isError]);

  // Suggested Article for URL ----
  const showNBROutside = Boolean(helpCenter?.next_best_action.enable_outside_next_best_resource);
  const [articlesReady, setArticlesReady] = useState(false);

  useEffect(() => {
    if (!isLoading && isFetched) {
      setArticlesReady(true);
    }
  }, [isLoading, isFetched]);

  const hardcodedSuggestions = useMemo(
    () => helpCenter?.next_best_action.hardcoded_outside_suggestions || {},
    [helpCenter?.next_best_action.hardcoded_outside_suggestions]
  );

  const getSuggestedArticleForUrl = useCallback(() => {
    if (!showNBROutside) return;

    const url = window.location.href;
    const matchedURL = Object.keys(hardcodedSuggestions).find((key) => url.includes(key));

    if (!matchedURL) return;

    const articles = hardcodedSuggestions[matchedURL];

    if (!articles.length) return;

    const article = articles.find((article) => !trackedArticles.current.has(article.id));

    if (!article) return;

    setSuggestedArticleForURL(article);
  }, [hardcodedSuggestions, showNBROutside]);

  useEffect(() => {
    urlChangeDetector.on(getSuggestedArticleForUrl);

    return () => urlChangeDetector.off(getSuggestedArticleForUrl);
  }, [getSuggestedArticleForUrl]);

  useEffect(() => {
    if (showNBROutside && isFetched) {
      getSuggestedArticleForUrl();
    }
  }, [hardcodedSuggestions, showNBROutside, getSuggestedArticleForUrl, isFetched]);

  // ----

  return (
    <SuggestedArticleContext.Provider
      value={{
        suggestedArticle,
        suggestedArticleForURL,
        isLoading,
        articlesReady,
        lastViewedArticle,
        setLastViewedArticle,
        suggestedArticleEnabled,
      }}
    >
      {children}
    </SuggestedArticleContext.Provider>
  );
};

export const useSuggestedArticle = () => useContext(SuggestedArticleContext);
